import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem, Stack } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import ProfessionalList from './professional/professional-list/professionalListRouter';
import RoomList from './rooms/roomList/roomListRouter';
import MedicineList from './medicines/medicineList/medicineListRouter';
import ProcedureCatalogList from './procedures/procedureCatalogList/procedureCatalogListRouter';
import ConsultationTypeList from './consultationTypes/consultationTypeList/consultationTypeListRouter';
import AnamnesisCatalogList from './anamnesisCatalog/anamnesisCatalogList/anamnesisCatalogListRouter';
import FinancialCategoriesTab from './financialCategories/financialCategoriesTab';
import CidList from './cidCatalog/cidList/cidListRouter';
import CityList from './cities/cityList/cityListRouter';
import CovenantList from './covenants/covenantList/covenantListRouter';
import LaboratoryList from './laboratories/laboratoryList/laboratoryListRouter';
import { useRoomLabel } from '../hooks/useRoomLabel';

const ROOM_SLUG = 'sala';

const registerGroups = [
  {
    key: 'structure',
    label: 'Equipe & Estrutura',
    items: [
      { slug: 'profissional', label: 'Profissional', Component: ProfessionalList },
      { slug: ROOM_SLUG, label: 'Sala', Component: RoomList },
    ],
  },
  {
    key: 'appointment',
    label: 'Atendimento',
    items: [
      { slug: 'tipos-de-consulta', label: 'Tipos de Consulta', Component: ConsultationTypeList },
      { slug: 'procedimentos', label: 'Procedimentos', Component: ProcedureCatalogList },
      { slug: 'anamnese', label: 'Anamnese', Component: AnamnesisCatalogList },
      { slug: 'convenios', label: 'Convênios', Component: CovenantList },
    ],
  },
  {
    key: 'clinical',
    label: 'Clínico',
    items: [
      { slug: 'medicamentos', label: 'Medicamentos', Component: MedicineList },
      { slug: 'cid', label: 'CID', Component: CidList },
      { slug: 'laboratorios', label: 'Laboratórios', Component: LaboratoryList },
    ],
  },
  {
    key: 'financial',
    label: 'Financeiro',
    items: [
      { slug: 'financeiro', label: 'Categorias Financeiras', Component: FinancialCategoriesTab },
    ],
  },
  {
    key: 'general',
    label: 'Gerais',
    items: [{ slug: 'cidades', label: 'Cidades', Component: CityList }],
  },
];

const registerItems = registerGroups.flatMap((group) => group.items);

export default function RegisterPage() {
  const roomLabel = useRoomLabel();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openGroupKey, setOpenGroupKey] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const currentSlug = searchParams.get('tab');
  const activeItem = registerItems.find((item) => item.slug === currentSlug) ?? registerItems[0];
  const activeGroupKey = registerGroups.find((group) =>
    group.items.some((item) => item.slug === activeItem.slug),
  ).key;

  const ActiveComponent = activeItem.Component;

  // A sala tem nome variável por clínica ("Consultório" ou "Sala").
  const getItemLabel = (item) => (item.slug === ROOM_SLUG ? roomLabel.singular : item.label);

  const openGroupMenu = (event, groupKey) => {
    setAnchorEl(event.currentTarget);
    setOpenGroupKey(groupKey);
  };

  const closeGroupMenu = () => {
    setAnchorEl(null);
    setOpenGroupKey(null);
  };

  const selectItem = (item) => {
    setSearchParams({ tab: item.slug });
    closeGroupMenu();
  };

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 1, md: 4 },
          pt: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
          {registerGroups.map((group) => {
            const isActiveGroup = group.key === activeGroupKey;

            return (
              <Button
                key={group.key}
                onClick={(event) => openGroupMenu(event, group.key)}
                endIcon={<ChevronDown size={16} />}
                color={isActiveGroup ? 'primary' : 'inherit'}
                data-cy={`register-group-${group.key}`}
                sx={{
                  flexShrink: 0,
                  fontWeight: isActiveGroup ? 600 : 400,
                  color: isActiveGroup ? 'primary.main' : 'text.secondary',
                  borderBottom: 2,
                  borderRadius: 0,
                  borderColor: isActiveGroup ? 'primary.main' : 'transparent',
                }}
              >
                {group.label}
              </Button>
            );
          })}
        </Stack>
      </Box>

      {registerGroups.map((group) => (
        <Menu
          key={group.key}
          anchorEl={anchorEl}
          open={openGroupKey === group.key}
          onClose={closeGroupMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {group.items.map((item) => (
            <MenuItem
              key={item.slug}
              selected={item.slug === activeItem.slug}
              onClick={() => selectItem(item)}
              data-cy={`register-tab-${item.slug}`}
            >
              {getItemLabel(item)}
            </MenuItem>
          ))}
        </Menu>
      ))}

      <ActiveComponent />
    </Box>
  );
}
